/**
 * Campaign times.
 *
 * On-chain, a campaign's window is two instants, `startAt` and `endAt` (specification §4.2).
 * X campaigns may start and end at any UTC instant. YouTube reports analytics per day in
 * America/Los_Angeles, so a YouTube campaign covers whole reporting dates: `startAt` is 00:00 in
 * Los Angeles on the first date and `endAt` is 00:00 on the day after the final date (§7.3).
 * These functions convert between reporting dates and instants, across daylight saving changes.
 */

/** The time zone of YouTube's reporting dates. */
export const YOUTUBE_TIME_ZONE = "America/Los_Angeles";

/** A calendar date, `YYYY-MM-DD`. */
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The instant at which `date` begins (00:00) in `timeZone`.
 *
 * @throws RangeError if `date` is not a real `YYYY-MM-DD` date.
 * @example zonedMidnight("2026-10-05", "America/Los_Angeles") // 2026-10-05T07:00:00.000Z (PDT)
 */
export function zonedMidnight(date: string, timeZone: string): Date {
  const match = DATE_PATTERN.exec(date);
  const [year, month, day] = match ? [Number(match[1]), Number(match[2]), Number(match[3])] : [];
  if (year === undefined || month === undefined || day === undefined) {
    throw new RangeError(`Not a date: "${date}"`);
  }
  const utcMidnight = Date.UTC(year, month - 1, day);
  if (new Date(utcMidnight).toISOString().slice(0, 10) !== date) {
    throw new RangeError(`Not a date: "${date}"`);
  }
  // Local midnight is UTC midnight minus the zone's offset. The offset is read twice because the
  // offset at UTC midnight can differ from the offset at local midnight on the day it changes.
  const firstGuess = utcMidnight - offsetMs(new Date(utcMidnight), timeZone);
  return new Date(utcMidnight - offsetMs(new Date(firstGuess), timeZone));
}

/** The calendar date (`YYYY-MM-DD`) of an instant in `timeZone`. */
export function zonedDate(instant: Date, timeZone: string): string {
  const parts = zonedParts(instant, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** Whether an instant is exactly 00:00:00 in `timeZone`. */
export function isZonedMidnight(instant: Date, timeZone: string): boolean {
  const parts = zonedParts(instant, timeZone);
  return (
    parts.hour === "00" &&
    parts.minute === "00" &&
    parts.second === "00" &&
    instant.getUTCMilliseconds() === 0
  );
}

/** The window `[startAt, endAt)` covering YouTube reporting dates `firstDate` to `finalDate`. */
export function youtubeWindow(
  firstDate: string,
  finalDate: string,
): { startAt: Date; endAt: Date } {
  const startAt = zonedMidnight(firstDate, YOUTUBE_TIME_ZONE);
  const finalStart = zonedMidnight(finalDate, YOUTUBE_TIME_ZONE);
  // 36 hours after the final date's midnight is the next day around noon in Los Angeles, whatever
  // the final date's length (23, 24 or 25 hours), so its date is the day after the final date.
  const nextDay = new Date(finalStart.getTime() + 36 * 60 * 60 * 1000);
  return {
    startAt,
    endAt: zonedMidnight(zonedDate(nextDay, YOUTUBE_TIME_ZONE), YOUTUBE_TIME_ZONE),
  };
}

/** The zone's offset from UTC at an instant, in milliseconds (e.g. -7 h for PDT). */
function offsetMs(instant: Date, timeZone: string): number {
  const parts = zonedParts(instant, timeZone);
  const asIfUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asIfUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Year, month, day, hour, minute and second of an instant in `timeZone`, as two-digit text. */
function zonedParts(instant: Date, timeZone: string): Record<string, string> {
  const format = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return Object.fromEntries(format.formatToParts(instant).map((part) => [part.type, part.value]));
}
